var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ladokApi } from "apis/eduidLadok";
import { useAppSelector } from "eduid-hooks";
import { useEffect, useMemo, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
export const LadokContainer = () => {
    const isLinked = useAppSelector((state) => state.ladok.isLinked);
    const [switchChecked, setSwitchChecked] = useState(isLinked);
    const [unlinkUser] = ladokApi.useLazyUnlinkUserQuery();
    const handleSwitchChange = () => __awaiter(void 0, void 0, void 0, function* () {
        const newChecked = !switchChecked;
        setSwitchChecked(newChecked);
        if (!newChecked && isLinked) {
            // User is turning switch off while linked - unlink
            const result = yield unlinkUser();
            if (result.isSuccess) {
                setSwitchChecked(false);
            }
        }
    });
    // Sync switch state with isLinked on mount/reload when Redux state is hydrated
    useEffect(() => {
        setSwitchChecked(isLinked);
    }, [isLinked]);
    return (_jsxs("article", { className: "ladok", id: "ladok", children: [_jsx("h2", { children: _jsx(FormattedMessage, { id: "ladok.esiInfoLabel", defaultMessage: "ESI information", description: "Ladok account linking" }) }), _jsx("p", { children: _jsx(FormattedMessage, { id: "ladok.fetchInfo", defaultMessage: "Data from Ladok might give you access to more services. Some higher education institutions allow eduID to fetch data from Ladok.", description: "Ladok account linking" }) }), _jsx("form", { children: _jsx("fieldset", { children: _jsxs("label", { className: "toggle flex-between", htmlFor: "ladok-connection", children: [_jsx("legend", { children: _jsx(FormattedMessage, { id: "ladok.linkHeading", defaultMessage: "Link your account to Ladok", description: "Ladok account linking" }) }), _jsx("input", { onChange: handleSwitchChange, className: "toggle-checkbox", type: "checkbox", checked: switchChecked, id: "ladok-connection" }), _jsx("div", { className: "toggle-switch" })] }) }) }), isLinked ? _jsx(LadokLinkStatus, {}) : undefined, switchChecked ? _jsx(LadokUniversitiesDropdown, { onLinkSuccess: () => setSwitchChecked(true) }) : undefined, _jsx("p", { className: "help-text", children: _jsx(FormattedMessage, { id: "ladok.notInListInfo", defaultMessage: "Linking your eduID account with data from Ladok is necessary if you want to access a service requiring a European Student Identifier.", description: "Ladok account linking" }) })] }));
};
const LadokUniversitiesDropdown = ({ onLinkSuccess }) => {
    const locale = useAppSelector((state) => state.intl.locale);
    const ladokUnis = useAppSelector((state) => state.ladok.unis);
    const fetchFailed = useAppSelector((state) => state.ladok.unisFetchFailed);
    const ladokName = useAppSelector((state) => state.ladok.ladokName);
    const intl = useIntl();
    const [fetchLadokUniversities] = ladokApi.useLazyFetchLadokUniversitiesQuery();
    const [linkUser] = ladokApi.useLazyLinkUserQuery();
    const placeholder = intl.formatMessage({
        id: "ladok.dropdownPlaceholder",
        defaultMessage: "Available higher education institutions",
        description: "Ladok account linking",
    });
    // if no university is selected, select box label will be placeholder
    const [selected, setSelected] = useState({ label: placeholder, value: "" });
    useEffect(() => {
        if (ladokUnis === undefined) {
            // initiate fetching of universities metadata when the user indicates they
            // are interested in linking their account
            fetchLadokUniversities();
        }
    }, [fetchLadokUniversities, ladokUnis]);
    function handleOnChange(newValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (newValue === null || newValue === void 0 ? void 0 : newValue.value) {
                setSelected(newValue);
                const result = yield linkUser({ ladok_name: newValue.value });
                if (result.isSuccess) {
                    onLinkSuccess();
                }
            }
        });
    }
    // Convert ladokUnis to an array of SelectedUniProps that works with the Select component
    const selectOptions = useMemo(() => {
        if (ladokUnis === undefined) {
            return [];
        }
        const res = [];
        Object.values(ladokUnis).forEach((item) => {
            // Get the name of the university in the users locale, fallback to English and then to ladok_name.
            const localised = item.name[locale] || item.name.en || item.ladok_name;
            const curr = { label: localised, value: item.ladok_name };
            // initialise 'selected'
            if (item.ladok_name === ladokName) {
                setSelected(curr);
            }
            res.push(curr);
        });
        return res;
    }, [ladokUnis, ladokName, locale]);
    return (_jsxs(_Fragment, { children: [_jsx(FinalForm, { onSubmit: () => { }, render: ({ handleSubmit }) => (_jsx("form", { onSubmit: handleSubmit, children: _jsxs("fieldset", { children: [_jsx("span", { className: "form-label", children: _jsx(FormattedMessage, { id: "ladok.selectInstitution", defaultMessage: "Select higher education institution", description: "Ladok account linking" }) }), _jsx(Select, { isDisabled: fetchFailed, options: selectOptions, onChange: handleOnChange, value: selected, isSearchable: false, className: "react-select-container", classNamePrefix: "react-select" })] }) })) }), _jsx("div", { className: "ladok-universities-status", children: fetchFailed ? (_jsx(FormattedMessage, { id: "ladok.listError", defaultMessage: "The list of higher education institutions could not be loaded at this time", description: "Ladok account linking" })) : undefined })] }));
};
const LadokLinkStatus = () => {
    const isLinked = useAppSelector((state) => state.ladok.isLinked);
    const unis = useAppSelector((state) => state.ladok.unis);
    const ladok_name = useAppSelector((state) => state.ladok.ladokName);
    const locale = useAppSelector((state) => state.intl.locale);
    // Initial university name will be empty string until information has been updated
    let university_name = "";
    if (unis && ladok_name && unis[ladok_name]) {
        if (locale) {
            const uni = unis[ladok_name];
            if (uni.name[locale]) {
                university_name = uni.name[locale];
            }
            else if (uni.name.en) {
                university_name = uni.name.en;
            }
        }
    }
    return (_jsx(_Fragment, { children: isLinked && (_jsx("fieldset", { children: _jsxs("div", { className: "ladok-university flex-between", children: [_jsx("label", { children: _jsx(FormattedMessage, { id: "ladok.linkedFrom", defaultMessage: "Your account is linked with Ladok information from", description: "Ladok account linking" }) }), _jsx("div", { className: "text-large ladok-university-name", children: university_name })] }) })) }));
};
